import React, { useEffect, useState } from 'react';

// Strips formatting from a phone number so it can be used in `tel:` and
// `wa.me/` URLs. Keeps a leading `+` if present.
const sanitizePhone = (raw) => {
  if (!raw) return '';
  const trimmed = String(raw).trim();
  const digits = trimmed.replace(/[^\d+]/g, '');
  return digits.startsWith('+') ? digits : `+${digits.replace(/^\+/, '')}`;
};

const FloatingActions = ({ settings, hidden = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 200);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-collapse the action sheet when navigating away or scrolling far.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('hashchange', close);
    return () => window.removeEventListener('hashchange', close);
  }, [open]);

  const phoneRaw =
    settings?.contactPhone || settings?.phone || '+387 61 680 413';
  const emailRaw =
    settings?.contactEmail || settings?.email || 'selimovicerminphoto@gmail.com';

  const phone = sanitizePhone(phoneRaw);
  const phoneTel = `tel:${phone}`;
  const phoneDigitsOnly = phone.replace(/\D/g, '');
  const whatsappUrl = `https://wa.me/${phoneDigitsOnly}?text=${encodeURIComponent(
    'Pozdrav, zanima me ponuda. 👋'
  )}`;
  const mailto = `mailto:${emailRaw}?subject=${encodeURIComponent('Upit sa sajta')}`;

  if (hidden) return null;

  return (
    <div
      className={`fixed z-[80] right-4 sm:right-6 bottom-4 sm:bottom-6 transition-all duration-500 ${
        scrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      {/* Action sheet */}
      <div
        className={`absolute bottom-full right-0 mb-3 flex flex-col gap-2 transition-all duration-300 origin-bottom-right ${
          open
            ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 scale-95 translate-y-2 pointer-events-none'
        }`}
      >
        <ActionItem
          href={whatsappUrl}
          label="WhatsApp"
          color="bg-emerald-500 text-white"
          icon={
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
            </svg>
          }
        />
        <ActionItem
          href={phoneTel}
          label="Pozovi"
          color="bg-gray-900 text-white"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h2.28a2 2 0 011.94 1.515l.7 2.793a2 2 0 01-.45 1.945L8.21 10.79a11.05 11.05 0 005 5l1.537-1.26a2 2 0 011.945-.45l2.793.7A2 2 0 0121 16.72V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          }
        />
        <ActionItem
          href={mailto}
          label="Email"
          color="bg-obsidian text-gold-light"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          }
        />
      </div>

      {/* Main toggle button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Zatvori akcije' : 'Brzi kontakt'}
        aria-expanded={open}
        className={`relative w-14 h-14 sm:w-16 sm:h-16 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 ${
          open
            ? 'bg-gray-900 rotate-90 scale-95'
            : 'bg-obsidian hover:scale-110'
        }`}
      >
        {!open && (
          <span className="absolute inset-0 rounded-full bg-gold/30 animate-ping" />
        )}
        {open ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-6 h-6 text-white relative z-10">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6 sm:w-7 sm:h-7 text-white relative z-10">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        )}
      </button>
    </div>
  );
};

const ActionItem = ({ href, label, color, icon }) => {
  const isExternal = href.startsWith('http');
  return (
    <a
      href={href}
      {...(isExternal
        ? { target: '_blank', rel: 'noopener noreferrer' }
        : {})}
      className="group flex items-center gap-3 pr-4 pl-1.5 py-1.5 bg-white shadow-xl rounded-full border border-gray-100 hover:-translate-x-0.5 transition-all"
    >
      <span
        className={`w-9 h-9 rounded-full flex items-center justify-center ${color} group-hover:scale-110 transition-transform shadow-md`}
      >
        {icon}
      </span>
      <span className="text-xs font-bold uppercase tracking-wider text-gray-900 whitespace-nowrap">
        {label}
      </span>
    </a>
  );
};

export default FloatingActions;
